<template>
  <span>
    <RecordFormDialog :visible.sync="innerVisible" :title="quoteFormTitle">
      <QuoteForm
        v-if="innerVisible"
        ref="quoteFormRef"
        :key="quoteFormKey"
        embedded
        :record-id="quoteFormRecordId"
        :initial-lead-id="normalizedLeadId"
        @saved="onQuoteFormSaved"
        @cancel="innerVisible = false"
        @saving="v => (quoteFormSaving = v)"
        @embedded-footer="onQuoteEmbeddedFooter"
        @create-contract-from-quote="onCreateContractFromQuote"
      />
      <template slot="footer">
        <el-button @click="innerVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button
          v-if="quoteFormViewContractId"
          type="warning"
          plain
          @click="goQuoteLinkedContractDetail"
        >{{ $t('quoteListPage.viewContractDetail') }}</el-button>
        <el-button
          v-if="quoteFormShowCreateContract"
          type="warning"
          plain
          @click="openContractFromQuoteInQuoteDialog"
        >{{ $t('quoteFormPage.createContractFromQuote') }}</el-button>
        <template v-if="!quoteFormLocked">
          <el-button
            type="success"
            plain
            :loading="quoteFormSaving"
            @click="submitQuoteApprovalDialog"
          >{{ $t('quoteFormPage.saveSubmitApproval') }}</el-button>
          <el-button
            type="primary"
            :loading="quoteFormSaving"
            @click="submitQuoteSaveDialog"
          >{{ $t('quoteFormPage.saveDraft') }}</el-button>
        </template>
        <el-button
          v-if="quoteFormShowDeleteDraft"
          type="danger"
          plain
          :loading="quoteFormSaving"
          @click="deleteQuoteDraftDialog"
        >{{ $t('quoteFormPage.deleteDraft') }}</el-button>
      </template>
    </RecordFormDialog>

    <RecordFormDialog :visible.sync="contractFromQuoteVisible" :title="$t('contractFromQuotePage.title')">
      <ContractFromQuote
        v-if="contractFromQuoteVisible"
        :key="contractFromQuoteKey"
        ref="contractFromQuoteRef"
        embedded
        :preset-quote-id="contractFromQuoteQuoteId"
        @saved="onContractFromQuoteSaved"
        @saving="v => (contractFromQuoteSaving = v)"
      />
      <template slot="footer">
        <el-button @click="contractFromQuoteVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button
          type="primary"
          :loading="contractFromQuoteSaving"
          @click="submitContractFromQuoteDialog"
        >{{ $t('contractFromQuotePage.submitOpenDetail') }}</el-button>
      </template>
    </RecordFormDialog>
  </span>
</template>

<script>
import RecordFormDialog from '@/components/RecordFormDialog.vue'
import QuoteForm from '@/views/quote/QuoteForm.vue'
import ContractFromQuote from '@/views/contract/ContractFromQuote.vue'
import { contractDetailLocation } from '@/utils/contractDetailNav'

export default {
  name: 'LeadQuoteDialog',
  components: { RecordFormDialog, QuoteForm, ContractFromQuote },
  props: {
    visible: { type: Boolean, default: false },
    /** 预选的线索 ID（来自线索列表/详情） */
    leadId: { type: [String, Number], default: null }
  },
  data() {
    return {
      quoteFormSaving: false,
      quoteFormLocked: false,
      quoteFormShowDeleteDraft: false,
      quoteFormShowCreateContract: false,
      quoteFormViewContractId: null,
      quoteFormRecordId: null,
      quoteFormKey: 0,
      contractFromQuoteVisible: false,
      contractFromQuoteSaving: false,
      contractFromQuoteQuoteId: null,
      contractFromQuoteKey: 0
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(v) {
        this.$emit('update:visible', v)
      }
    },
    quoteFormTitle() {
      return this.quoteFormRecordId
        ? this.$t('quoteFormPage.editTitle')
        : this.$t('quoteFormPage.newTitle')
    },
    normalizedLeadId() {
      const lid = this.leadId
      if (lid === undefined || lid === '' || lid == null) return null
      const n = Number(lid)
      return Number.isFinite(n) ? n : null
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.prepareOpen()
      }
    }
  },
  methods: {
    prepareOpen() {
      this.resetQuoteFormUi()
      this.quoteFormRecordId = null
      this.quoteFormKey += 1
    },
    resetQuoteFormUi() {
      this.quoteFormLocked = false
      this.quoteFormShowDeleteDraft = false
      this.quoteFormShowCreateContract = false
      this.quoteFormViewContractId = null
    },
    onQuoteEmbeddedFooter(p) {
      if (!p) return
      this.quoteFormLocked = !!p.formLocked
      this.quoteFormShowDeleteDraft = !!p.showDeleteDraft
      this.quoteFormShowCreateContract = !!p.showCreateContractFromQuote
      const vid = p.viewContractId
      this.quoteFormViewContractId =
        vid != null && vid !== '' && Number.isFinite(Number(vid)) ? Number(vid) : null
    },
    goQuoteLinkedContractDetail() {
      const id = this.quoteFormViewContractId
      if (id == null) return
      this.innerVisible = false
      this.$router.push(contractDetailLocation(id, this.$route.fullPath))
    },
    openContractFromQuoteInQuoteDialog() {
      if (this.$refs.quoteFormRef) this.$refs.quoteFormRef.openContractFromQuote()
    },
    submitQuoteSaveDialog() {
      if (this.$refs.quoteFormRef) this.$refs.quoteFormRef.save()
    },
    submitQuoteApprovalDialog() {
      if (this.$refs.quoteFormRef) this.$refs.quoteFormRef.submitForApproval()
    },
    deleteQuoteDraftDialog() {
      if (this.$refs.quoteFormRef) this.$refs.quoteFormRef.deleteDraft()
    },
    onCreateContractFromQuote({ quoteId }) {
      const qid = quoteId != null && quoteId !== '' ? Number(quoteId) : null
      if (!qid) return
      this.innerVisible = false
      this.contractFromQuoteQuoteId = qid
      this.contractFromQuoteKey += 1
      this.$nextTick(() => {
        this.contractFromQuoteVisible = true
      })
    },
    submitContractFromQuoteDialog() {
      if (this.$refs.contractFromQuoteRef) this.$refs.contractFromQuoteRef.submit()
    },
    onContractFromQuoteSaved({ id }) {
      this.contractFromQuoteVisible = false
      this.$emit('saved')
      if (id != null && id !== '') this.$router.push(contractDetailLocation(id, this.$route.fullPath))
    },
    onQuoteFormSaved() {
      this.innerVisible = false
      this.$emit('saved')
    }
  }
}
</script>
